import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import { FC } from 'react'
import { format } from 'date-fns'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import InfoIcon from '@mui/icons-material/Info'
import { AccountingPeriod, getMonthName, getQuarterName } from './types'
import { PeriodStatus } from '../../../../generated'

interface PeriodStatusListingProps {
  periods: AccountingPeriod[]
  fiscalYear: number
  onUnclose: (period: AccountingPeriod) => void
  loading?: boolean
}

export const PeriodStatusListing: FC<PeriodStatusListingProps> = ({
  periods,
  fiscalYear,
  onUnclose,
  loading = false
}) => {
  const getStatusColor = (status: PeriodStatus) => {
    return status === PeriodStatus.Closed ? 'success' : 'default'
  }

  const getStatusIcon = (status: PeriodStatus) => {
    return status === PeriodStatus.Closed ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />
  }

  const canUnclose = (period: AccountingPeriod, allPeriods: AccountingPeriod[]): boolean => {
    if (period.status !== PeriodStatus.Closed) return false

    // Check if any later months are closed
    const laterClosedMonths = allPeriods.filter(
      p => p.fiscalYear === period.fiscalYear && p.month > period.month && p.status === PeriodStatus.Closed
    )

    return laterClosedMonths.length === 0
  }

  // Create array of all 12 months with their status
  const monthsData = Array.from({ length: 12 }, (_, index) => {
    const month = index + 1
    const period = periods.find(p => p.fiscalYear === fiscalYear && p.month === month)

    return {
      month,
      period,
      status: period?.status || PeriodStatus.Open
    }
  })

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell>
              <strong>Month</strong>
            </TableCell>
            <TableCell>
              <strong>Quarter</strong>
            </TableCell>
            <TableCell>
              <strong>Period</strong>
            </TableCell>
            <TableCell>
              <strong>Status</strong>
            </TableCell>
            <TableCell>
              <strong>Closed Date</strong>
            </TableCell>
            <TableCell>
              <strong>Closed By</strong>
            </TableCell>
            <TableCell>
              <strong>Notes</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Actions</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography>Loading...</Typography>
              </TableCell>
            </TableRow>
          ) : (
            monthsData.map(({ month, period, status }) => (
              <TableRow key={month} hover>
                <TableCell>
                  <Typography variant="body2">
                    <strong>{getMonthName(month)}</strong>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {getQuarterName(month)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(fiscalYear, month - 1, 1), 'MMM 1')} -{' '}
                    {format(new Date(fiscalYear, month, 0), 'MMM d')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={status.toUpperCase()}
                    color={getStatusColor(status)}
                    size="small"
                    icon={getStatusIcon(status)}
                  />
                </TableCell>
                <TableCell>
                  {period?.closedAt ? (
                    <Typography variant="body2">{format(new Date(period.closedAt), 'MMM d, yyyy h:mm a')}</Typography>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {period?.closedBy ? (
                    <Typography variant="body2">
                      {period.closedBy.firstName} {period.closedBy.lastName}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {period?.notes ? (
                    <Tooltip title={period.notes}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {period.notes}
                        </Typography>
                        <InfoIcon fontSize="small" color="action" />
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {period && status === PeriodStatus.Closed && (
                    <Tooltip
                      title={
                        canUnclose(period, periods)
                          ? 'Unclose this month'
                          : 'Cannot unclose. Later months are closed. Unclose them first.'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onUnclose(period)}
                          // DOESN'T ALLOW UNCLOSE ADV DATA PERIODS
                          disabled={!canUnclose(period, periods) || fiscalYear < 2026}
                        >
                          <LockOpenIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
